import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Grid,
  Chip,
  Autocomplete,
} from '@mui/material';
import { Science } from '@mui/icons-material';
import { Button, Card, LoadingSpinner, ErrorMessage } from '@/components/ui';
import { ingredientService } from '@/services/ingredient.service';
import { allergenService } from '@/services/allergen.service';
import { useToastStore } from '@/store/toastStore';
import { MEASUREMENT_UNITS } from '@/constants/measurementUnits';
import type { IngredientCategory, CreateIngredientInput } from '@/types';

// Schéma de validation Zod
const ingredientSchema = z.object({
  name: z
    .string()
    .min(1, "Le nom est obligatoire")
    .min(2, "Le nom doit contenir au moins 2 caractères")
    .max(255, "Le nom ne peut pas dépasser 255 caractères"),
  category: z.enum(
    ['MEAT', 'FISH', 'VEGETABLE', 'STARCH', 'DAIRY', 'SPICE', 'FRUIT', 'OTHER'],
    {
      required_error: "La catégorie est obligatoire",
    }
  ),
  isVegetarian: z.boolean().default(false),
  isVegan: z.boolean().default(false),
  unit: z.string().max(20).optional(),
  notes: z.string().optional(),
  allergenIds: z.array(z.string()).optional().default([]),
});

type IngredientFormData = z.infer<typeof ingredientSchema>;

/**
 * Page de formulaire pour créer ou éditer un ingrédient
 */
export function IngredientFormPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  // Récupérer les allergènes
  const { data: allergens = [], isLoading: allergensLoading } = useQuery({
    queryKey: ['allergens'],
    queryFn: () => allergenService.getAll(),
  });

  // Récupérer l'ingrédient si édition
  const {
    data: ingredient,
    isLoading: ingredientLoading,
    error: ingredientError,
  } = useQuery({
    queryKey: ['ingredients', id],
    queryFn: () => ingredientService.getById(id!),
    enabled: isEditing,
  });

  // Formulaire
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm<IngredientFormData>({
    resolver: zodResolver(ingredientSchema),
    defaultValues: {
      name: '',
      category: 'OTHER',
      isVegetarian: false,
      isVegan: false,
      unit: '',
      notes: '',
      allergenIds: [],
    },
  });

  // Watch isVegan pour auto-cocher isVegetarian
  const isVegan = watch('isVegan');

  // Remplir le formulaire avec les données de l'ingrédient si édition
  useEffect(() => {
    if (ingredient) {
      reset({
        name: ingredient.name,
        category: ingredient.category,
        isVegetarian: ingredient.isVegetarian,
        isVegan: ingredient.isVegan,
        unit: ingredient.unit || '',
        notes: ingredient.notes || '',
        allergenIds: ingredient.allergens.map((ia) => ia.allergen.id),
      });
    }
  }, [ingredient, reset]);

  // Si vegan, alors forcément végétarien
  useEffect(() => {
    if (isVegan) {
      // Auto-set vegetarian if vegan is checked
      // This will be handled by validation or we can force it
    }
  }, [isVegan]);

  // Store pour les toasts
  const { success, error: showError } = useToastStore();

  // Mutation pour créer
  const createMutation = useMutation({
    mutationFn: (data: CreateIngredientInput) => ingredientService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredients'] });
      success('Ingrédient créé avec succès');
      navigate('/ingredients');
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la création de l'ingrédient"
      );
    },
  });

  // Mutation pour mettre à jour
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateIngredientInput>;
    }) => ingredientService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredients'] });
      queryClient.invalidateQueries({ queryKey: ['ingredients', id] });
      success('Ingrédient modifié avec succès');
      navigate('/ingredients');
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la modification de l'ingrédient"
      );
    },
  });

  const onSubmit = (data: IngredientFormData) => {
    // Si vegan, forcer vegetarian à true
    const finalData: CreateIngredientInput = {
      ...data,
      category: data.category as IngredientCategory,
      isVegetarian: data.isVegan ? true : data.isVegetarian,
      unit: data.unit || undefined,
      notes: data.notes || undefined,
    };

    if (isEditing && id) {
      updateMutation.mutate({ id, data: finalData });
    } else {
      createMutation.mutate(finalData);
    }
  };

  // Formater la catégorie
  const categoryLabels: Record<IngredientCategory, string> = {
    MEAT: 'Viande',
    FISH: 'Poisson',
    VEGETABLE: 'Légume',
    STARCH: 'Féculent',
    DAIRY: 'Produit laitier',
    SPICE: 'Épice',
    FRUIT: 'Fruit',
    OTHER: 'Autre',
  };

  const isLoading = allergensLoading || (isEditing && ingredientLoading);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isEditing && ingredientError) {
    return (
      <ErrorMessage
        message="Erreur lors du chargement de l'ingrédient"
        onRetry={() =>
          queryClient.invalidateQueries({ queryKey: ['ingredients', id] })
        }
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* En-tête */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 900,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Science sx={{ fontSize: 32 }} />
          {isEditing ? 'Modifier l\'ingrédient' : 'Nouvel ingrédient'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {isEditing
            ? 'Modifiez les informations de l\'ingrédient'
            : 'Ajoutez un nouvel ingrédient à votre base'}
        </Typography>
      </Box>

      {/* Formulaire */}
      <Card sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {/* Nom */}
            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nom de l'ingrédient"
                    fullWidth
                    required
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    placeholder="Ex: Tomate, Poulet, Riz..."
                  />
                )}
              />
            </Grid>

            {/* Catégorie */}
            <Grid item xs={12} md={6}>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required error={!!errors.category}>
                    <InputLabel>Catégorie</InputLabel>
                    <Select {...field} label="Catégorie">
                      {Object.entries(categoryLabels).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.category && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {errors.category.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Unité */}
            <Grid item xs={12} md={6}>
              <Controller
                name="unit"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.unit}>
                    <InputLabel>Unité de mesure</InputLabel>
                    <Select
                      {...field}
                      label="Unité de mesure"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value || undefined)}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Sans unité</em>
                      </MenuItem>
                      {MEASUREMENT_UNITS.map((u) => (
                        <MenuItem key={u.value} value={u.value}>
                          {u.label}
                        </MenuItem>
                      ))}
                      {field.value && !MEASUREMENT_UNITS.some((u) => u.value === field.value) && (
                        <MenuItem value={field.value}>
                          {field.value} (personnalisé)
                        </MenuItem>
                      )}
                    </Select>
                    {errors.unit && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {errors.unit.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Allergènes */}
            <Grid item xs={12} md={6}>
              <Controller
                name="allergenIds"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={allergens}
                    getOptionLabel={(option) => `${option.icon} ${option.name}`}
                    value={allergens.filter((a) => field.value.includes(a.id))}
                    onChange={(_, newValue) => {
                      field.onChange(newValue.map((v) => v.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Allergènes"
                        placeholder="Sélectionnez les allergènes..."
                        helperText="Allergènes contenus dans cet ingrédient"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <span>{option.icon}</span>
                              <span>{option.code}</span>
                            </Box>
                          }
                          size="small"
                          sx={{
                            borderColor: 'error.main',
                            color: 'error.main',
                          }}
                        />
                      ))
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                )}
              />
            </Grid>

            {/* Régimes alimentaires */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
              >
                Régimes alimentaires
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Controller
                  name="isVegetarian"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || isVegan}
                          disabled={isVegan}
                          color="success"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Végétarien
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ne contient pas de viande/poisson
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                />
                <Controller
                  name="isVegan"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} color="success" />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Vegan
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Sans produit animal (implique végétarien)
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </Box>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes (optionnel)"
                    fullWidth
                    multiline
                    rows={3}
                    error={!!errors.notes}
                    helperText={
                      errors.notes?.message ||
                      'Informations complémentaires sur l\'ingrédient'
                    }
                    placeholder="Ex: Origine, conservation, remarques..."
                  />
                )}
              />
            </Grid>

            {/* Actions */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'flex-end',
                  pt: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Button
                  variant="outline"
                  onClick={() => navigate('/ingredients')}
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {isEditing ? 'Modifier' : 'Créer'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Card>
    </Box>
  );
}
